
import { getDatabase, ref as dbRef, push } from "firebase/database";

/**
 * Minimal helper: Push one “patient_added” (or “patient_creation_failed”) entry into
 * audit_logs/UserManagement/Patient_Creation
 *
 * @param {Object} options
 * @param {string}  options.patientIdEncrypted             – AES‐encrypted patient ID 
 * @param {string}  options.createdBy                       – Admin’s username
 * @param {string}  options.patientName                     – Plaintext patient name
 * @param {boolean} options.uploaded3D                       – true if a 3D file was uploaded
 * @param {boolean} options.uploadedReport                   – true if a report file was uploaded
 * @param {boolean} options.uploadedDicom                    – true if a DICOM folder was uploaded
 * @param {string}  options.modality                         – “CT”, “MRI”, “XRAY” or “”
 * @param {string}  options.category                         – The chosen test name (or custom text)
 * @param {boolean} options.clinicalHistoryEntered           – true if clinical‐history textarea is non‐empty
 * @param {boolean} options.clinicalHistoryFileAttached      – true if a clinical‐history file is attached
 * @param {boolean} options.success                           – true if the overall “add patient” succeeded
 * @param {string}  options.failureReason                     – short code (“duplicate_id”, “insufficient_balance”, “upload_or_db_error”, etc.) or “” on success
 */
export async function logPatientCreation({
  patientIdEncrypted,
  createdBy,
  patientName,
  uploaded3D,
  uploadedReport,
  uploadedDicom,
  modality = "",                        // default to empty string
  category = "",                        // default to empty string
  clinicalHistoryEntered = false,       // default to false
  clinicalHistoryFileAttached = false,  // default to false
  success,
  failureReason = "",
}) {
  try {
    const db = getDatabase();
    const logNodeRef = dbRef(
      db,
      "audit_logs/UserManagement/Patient_Creation"
    );

    // 1) Timestamp
    const timestamp = new Date().toISOString();

    // 2) Browser & device
    const userAgent = typeof window !== "undefined" ? navigator.userAgent : "";
    const deviceType = /Mobi|Android/i.test(userAgent) ? "Mobile" : "Desktop";

    // 3) IP & location via ipapi.co (best‐effort)
    const details = {
      action:    success ? "New patient added" : "Patient creation failed",
      ip:        "Unknown",
      browser:   userAgent,
      device:    deviceType,
      location:  "Unknown",
    };
    try {
      const resp = await fetch("https://ipapi.co/json/");
      const loc  = await resp.json();
      details.ip       = loc.ip       || "Unknown";
      details.location = `${loc.city  || "Unknown"}, ${loc.country_name || "Unknown"}`;
    } catch {
      /* ignore fetch errors */
    }

 
// 4) Build log entry
// const logEntry = {
//     event_type:                     success
//                                       ? "patient_added"
//                                       : "patient_creation_failed",
//     created_by:                     createdBy,
//     ...(success && { patient_id_encrypted: patientIdEncrypted }),
//     patient_name:                   patientName,
//     modality:                       modality,
//     category:                       category,
//     uploaded_3d:                    uploaded3D,
//     uploaded_report:                uploadedReport,
//     uploaded_dicom:                 uploadedDicom,
//     clinical_history_entered:       clinicalHistoryEntered,
//     clinical_history_file_attached: clinicalHistoryFileAttached,
//     success:                        success,
//     // Only include failure_reason when success === false
//     ...(!success && { failure_reason: failureReason }),
//     timestamp:                      timestamp,
//     details:                        details,
//   };

// 4) Build log entry
const logEntry = {
    event_type:   success ? "patient_added" : "patient_creation_failed",
    created_by:   createdBy,
    patient_name: patientName,
    success:      success,
    timestamp:    timestamp,
    details:      details,
  
    // Keep your existing “patient_id_encrypted” logic unchanged:
    ...(success && { patient_id_encrypted: patientIdEncrypted }),
  
    // Only include “failure_reason” when success === false:
    ...(!success && { failure_reason: failureReason }),
  
    // Only include “modality” if nonempty:
    ...(modality.trim() !== "" && { modality }),
  
    // Only include “category” if nonempty:
    ...(category.trim() !== "" && { category }),
  
    // Only include these upload‐flags if they’re true:
    ...(uploaded3D               && { uploaded_3d: true }),
    ...(uploadedReport           && { uploaded_report: true }),
    ...(uploadedDicom            && { uploaded_dicom: true }),
    ...(clinicalHistoryEntered   && { clinical_history_entered: true }),
    ...(clinicalHistoryFileAttached && { clinical_history_file_attached: true }),
  };
  
    // 5) Push under audit_logs/UserManagement/Patient_Creation
    await push(logNodeRef, logEntry);
  } catch (err) {
    console.error("Failed to write audit log:", err);
  }
}
